import {
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Button,
  Menu,
  Box,
  Typography,
  Checkbox,
  FormControl,
  FormGroup as MuiFormGroup,
  Badge,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { statisticsApi, organizationsApi } from "api";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ProcessesList from "./ProcessesList";
import { StartedProcess } from "./ProcessItem";
import { useForm, useTranslations } from "hooks";
import { getTranslation } from "common";
import FilterListIcon from "@mui/icons-material/FilterList";
import SortIcon from "@mui/icons-material/Sort";
import { SelectedOrganizationType } from "model/organizationModel";
import ProcessModelFilter from "./ProccessModelFilter/ProcessModelFilter";
import { ProcessModelType } from "model/processModel";
import {
  SortCriterion,
  checkUserDataSortType,
  customSort,
  getUserData,
  hasDuplicates,
} from "common/utilities";
import { SortState } from "hooks/useSortableList";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import CheckIcon from "@mui/icons-material/Check";
import { User } from "./UserItem";
import { Group } from "./GroupsPanel";
import { Role } from "./RolesPanel";
import { Robot } from "hooks/useRobots";

interface Data<T> {
  loaded: boolean;
  data: T[];
}
interface PageData {
  users: Data<User>;
  robots: Data<Robot>;
  groups: Data<Group>;
  processes: Data<StartedProcess>;
  roles: Data<Role>;
  processModels: Data<ProcessModelType>;
  userDataKeys: Data<string>;
}
interface ProcessStartedProps {
  organization: SelectedOrganizationType;
}
const getUserDataKeysFromProcesses = (processes: StartedProcess[]) => {
  const keys = new Set<string>();
  processes.forEach((process) => {
    try {
      const userData = getUserData(process.process_userdata);
      Object.keys(userData).forEach((key) => keys.add(key));
    } catch (e) { }
  });
  return Array.from(keys);
};

const LOCAL_STORAGE_KEY = `processesFilters`;
const ProcessStarted: React.FC<ProcessStartedProps> = ({ organization }) => {
  const translations = useTranslations();
  const [showAllGroups, setShowAllGroups] = useState(false);
  const [toDo, setToDo] = useState<boolean>(true);
  const [doing, setDoing] = useState<boolean>(true);
  const { values, changeHandler, setValues } = useForm(
    {
      status: "current",
      modelids: [],
      activityids: [],
      userids: [],
      groupids: [],
      roleids: [],
      search: "",
      userDataKeys: [],
    },
    []
  );

  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [sortAnchorEl, setSortAnchorEl] = useState<HTMLElement | null>(null);
  const [userDataMenuAnchorEl, setUserDataMenuAnchorEl] = useState<HTMLElement | null>(null);

  const handleUserDataClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserDataMenuAnchorEl(event.currentTarget);
  };

  const handleCloseUserData = () => {
    setUserDataMenuAnchorEl(null);
  };
  const [activities, setActivities] = useState<Array<string | null>>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [sortedProcesses, setSortedProcesses] = useState<StartedProcess[]>([]);
  const [newSort, setNewSort] = useState<SortCriterion>({});
  const [sortField, setSortField] = useState<string | null>(null);
  const [loading, setLoading] = useState(false)

  const [showAllActivities, setShowAllActivities] = useState(false);
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [showAllUserDataKeys, setShowAllUserDataKeys] = useState(false);
  const [showAllRoles, setShowAllRoles] = useState(false);
  const [pageData, setPageData] = useState<PageData>({
    users: { data: [], loaded: false },
    robots: { data: [], loaded: false },
    groups: { data: [], loaded: false },
    processes: { data: [], loaded: false },
    roles: { data: [], loaded: false },
    processModels: { data: [], loaded: false },
    userDataKeys: { data: [], loaded: false },
  });

  const processes = useMemo(() => pageData.processes.data, [pageData]);
  const users = useMemo(() => pageData.users.data, [pageData]);
  const processModels = useMemo(() => pageData.processModels.data, [pageData]);
  const groups = useMemo(() => pageData.groups.data, [pageData]);
  const roles = useMemo(() => pageData.roles.data, [pageData]);
  const allUserDataKeys = useMemo(() => pageData.userDataKeys.data, [pageData]);

  const setUsers = (users: User[]) => {
    setPageData((prev) => ({ ...prev, users: { data: users, loaded: true } }));
  };
  const setGroups = (groups: Group[]) => {
    setPageData((prev) => ({
      ...prev,
      groups: { data: groups, loaded: true },
    }));
  };
  const setRoles = (roles: Role[]) => {
    setPageData((prev) => ({ ...prev, roles: { data: roles, loaded: true } }));
  };
  const setRobots = (robots: Robot[]) => {
    setPageData((prev) => ({
      ...prev,
      robots: { data: robots, loaded: true },
    }));
  };
  // FASE 1: arrivo sulla pagina
  // FASE 2: cambio organizzazione
  // FASE 3: esco dalla pagina

  // FASE 1: inizializzo filtri
  // FASE 2: modifico filtri

  const handleChangeSort = (key: string) => {
    setNewSort((_) => {
      switch (_[key]) {
        case undefined:
        case SortState.None:
          return { [key]: SortState.Desc };
        case SortState.Asc:
          return { [key]: SortState.None };
        case SortState.Desc:
          return { [key]: SortState.Asc };
      }
    });
  };

  const sortProcesses = useCallback(
    (processes: StartedProcess[]) => {
      if (newSort.activity) {
        return [...processes].sort((a, b) => {
          const nameA = a.flownodename?.toLowerCase();
          const nameB = b.flownodename?.toLowerCase();
          if (newSort.activity === SortState.Asc) {
            return (nameA || "").localeCompare(nameB || "");
          } else {
            return (nameB || "").localeCompare(nameA || "");
          }
        });
      }
      if (newSort.model) {
        return [...processes].sort((a, b) => {
          const nameA = a.process_description.toLowerCase();
          const nameB = b.process_description.toLowerCase();
          if (newSort.model === SortState.Asc) {
            return nameA.localeCompare(nameB);
          } else {
            return nameB.localeCompare(nameA);
          }
        });
      }
      if (newSort.processmodelname) {
        return [...processes].sort((a, b) => {
          const nameA = a.processmodelname.toLowerCase();
          const nameB = b.processmodelname.toLowerCase();
          if (newSort.processmodelname === SortState.Asc) {
            return nameA.localeCompare(nameB);
          } else {
            return nameB.localeCompare(nameA);
          }
        });
      }
      if (newSort.process) {
        return [...processes].sort((a, b) => {
          const dateA = a.process_startdate
            ? new Date(a.process_startdate).getTime()
            : 0;
          const dateB = b.process_startdate
            ? new Date(b.process_startdate).getTime()
            : 0;
          return newSort.process === SortState.Asc
            ? dateA - dateB
            : dateB - dateA;
        });
      }
      if (newSort.activityStart) {
        return [...processes].sort((a, b) => {
          const dateA = a.created ? new Date(a.created).getTime() : 0;
          const dateB = b.created ? new Date(b.created).getTime() : 0;
          return newSort.activityStart === SortState.Asc
            ? dateA - dateB
            : dateB - dateA;
        });
      }

      const sortedProcesses = [...processes];
      Object.keys(newSort).forEach((key) => {
        if (newSort[key] !== SortState.None) {
          const userDataSortType = checkUserDataSortType(
            sortedProcesses.map((a) => a.process_userdata),
            newSort[key],
            key
          );
          sortedProcesses.sort((a, b) => {
            const userDataA = getUserData(a.process_userdata);
            const userDataB = getUserData(b.process_userdata);
            const valueA = userDataA[key] ? userDataA[key].value : "";
            const valueB = userDataB[key] ? userDataB[key].value : "";
            return newSort[key] === SortState.Asc
              ? customSort(valueA, valueB, userDataSortType)
              : customSort(valueB, valueA, userDataSortType);
          });
        }
      });
      return sortedProcesses;
    },
    [newSort]
  );

  useEffect(() => {
    const filteredProcesses = processes.filter((process) => {
      const matchModels =
        values.modelids.length === 0 ||
        values.modelids.includes(process.process_idprocessmodel);
      const matchActivities =
        values.activityids.length === 0 ||
        values.activityids.includes(process.flownodename);
      const matchUserDataKeys =
        values.userDataKeys.length === 0 ||
        hasDuplicates([
          ...values.userDataKeys,
          ...Object.keys(getUserData(process.process_userdata)),
        ]);
      const matchUsers =
        values.userids.length === 0 ||
        values.userids.includes(String(process.iduser));
      const matchGroups =
        values.groupids.length === 0 ||
        users.some((user) => {
          const isUserMatch = user.id === process.iduser;
          if (isUserMatch) {
            const isGroupMatch = user.groups.some((group) =>
              values.groupids.includes(String(group.id))
            );
            return isGroupMatch;
          }
          return false;
        });

      const matchRoles =
        values.roleids.length === 0 ||
        users.some((user) => {
          const isUserMatch = user.id === process.iduser;

          if (isUserMatch) {
            const isRoleMatch = user.roles.some((role) =>
              values.roleids.includes(String(role.id))
            );
            return isRoleMatch;
          }
          return false;
        });

      const matchSearch = values.search
        ? process.processmodelname
          .toLowerCase()
          .includes(values.search.toLowerCase()) ||
        process.flownodename
          ?.toLowerCase()
          .includes(values.search.toLowerCase()) ||
        process.process_description
          .toLowerCase()
          .includes(values.search.toLowerCase()) ||
        users.some(
          (user) =>
            user.id === process.iduser &&
            (user.firstname
              .toLowerCase()
              .includes(values.search.toLowerCase()) ||
              user.lastname
                .toLowerCase()
                .includes(values.search.toLowerCase()))
        ) ||
        Object.entries(getUserData(process.process_userdata)).some(
          ([key, value]) => {
            const _value = value.value;
            return (
              key.toLowerCase().includes(values.search.toLowerCase()) ||
              (typeof _value === "string"
                ? _value.toLowerCase().includes(values.search.toLowerCase())
                : String(_value).includes(values.search))
            );
          }
        )
        : true;

      const matchStatus =
        values.status === "current" ||
        (values.status === "doing" ? process.state === 1 : process.state === 0);
      return (
        matchModels &&
        matchActivities &&
        matchUsers &&
        matchSearch &&
        matchStatus &&
        matchUserDataKeys &&
        matchRoles &&
        matchGroups
      );
    });

    const sortedProcesses = sortProcesses(filteredProcesses);
    setSortedProcesses(sortedProcesses);
  }, [processes, sortProcesses, values, users, newSort]);

  // fetch dei dati iniziali (processi, modelli e utenti) all'inizializzazione del componente
  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [{ data: processesData }, { data }] = await Promise.all([
        statisticsApi.get(`current/${organization.id}`),
        organizationsApi.get(`/${organization?.id}/processmodels/v2`),
      ]);
      const activeProcessModels = data.processmodels.filter((model: any) =>
        processesData.data.some(
          (process: StartedProcess) =>
            process.process_idprocessmodel === model.id
        )
      );
      setPageData((prev) => ({
        ...prev,
        processes: { data: processesData.data, loaded: true },
        processModels: { data: activeProcessModels, loaded: true },
        userDataKeys: {
          data: getUserDataKeysFromProcesses(processesData.data),
          loaded: true,
        },
      }));
      return processesData.data as StartedProcess[];
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false)
    }
  }, [organization]);

  // fetch degli utenti dell'organizzazione
  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await organizationsApi.get(`${organization.id}/users`);
      if (Array.isArray(data.users)) {
        setUsers(data.users);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    }
  }, [organization]);

  // useEffect(() => {
  //   // solo user che hanno modeli attivi
  //   const activeUserIds = new Set(
  //     processes
  //       .filter((process) => process.state === 1) // questi sono i processi in corso
  //       .map((process) => process.iduser) // e qua li mappiamo per utente
  //   );

  //   const activeUsers = users.filter((user) => activeUserIds.has(user.id));
  //   setFilteredUsers(activeUsers);
  // }, [users, processes]);

  useEffect(() => {
    setFilteredUsers(users);
  }, [users]);

  // fetch dei gruppi dell'organizzazione
  const fetchGroups = useCallback(async () => {
    try {
      const { data } = await organizationsApi.get(`${organization.id}/groups`);
      if (Array.isArray(data.groups)) {
        setGroups(data.groups);
      } else {
        setGroups([]);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
      setGroups([]);
    }
  }, [organization]);

  const fetchRoles = useCallback(async () => {
    try {
      const { data } = await organizationsApi.get(`${organization.id}/roles`);
      if (Array.isArray(data.roles)) {
        setRoles(data.roles);
      } else {
        setRoles([]);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      setRoles([]);
    }
  }, [organization]);

  const fetchRobots = useCallback(async () => {
    try {
      const { data } = await organizationsApi.get(`${organization.id}/robots`);
      if (Array.isArray(data.robots)) {
        setRobots(data.robots);
      } else {
        setRobots([]);
      }
    } catch (error) {
      setRobots([]);
    }
  }, [organization]);

  useEffect(() => {
    setNewSort({});
  }, [sortField]);

  useEffect(() => {
    fetchUsers();
    fetchGroups();
    fetchRoles();
    fetchRobots();
  }, [fetchUsers, fetchGroups, fetchRoles, fetchRobots]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleClearFilters = useCallback(
    (disableLocalStorage = false, clearUserData = false) => {
      setValues((prev: any) => ({
        status: "current",
        modelids: [],
        activityids: [],
        userids: [],
        groupids: [],
        roleids: [],
        search: "",
        userDataKeys: clearUserData ? [] : prev.userDataKeys, // mantieni userDataKeys se clearUserData è false; spoiler: clearuserdata:true non lo sarà mai al momento
      }));
      if (!disableLocalStorage) {
        localStorage.removeItem(`${LOCAL_STORAGE_KEY}-${organization.id}`);
      }
    },
    [setValues, organization]
  );

  const handleClearUserdata = useCallback(() => {
    setValues((prev: any) => ({
      ...prev,
      userDataKeys: [],
    }));
  }, [setValues]);

  useEffect(() => {
    return () => handleClearFilters(true);
  }, [handleClearFilters]);

  useEffect(() => {
    return () => handleClearUserdata();
  }, [handleClearUserdata]);

  useEffect(() => {
    const value = doing && toDo ? "current" : doing ? "doing" : "todo";
    changeHandler("status", value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doing, toDo]);

  const extractUniqueActivities = (processes: StartedProcess[]) => {
    // qua le estraiamo (le attività uniché sennò mi vengono i doppioni sul filtro)
    return Array.from(
      new Set(
        processes.map((process) =>
          process.flownodetype === 0 && process.idtype === 3
            ? getTranslation(translations, "events.intermediate_catch")
            : process.flownodename
        )
      )
    );
  };

  useEffect(() => {
    setActivities(extractUniqueActivities(processes));
  }, [processes]);

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
  };

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleCheckboxChange = (category: string, value: string | null) => {
    changeHandler(
      category,
      values[category].includes(value)
        ? values[category].filter((v: string) => v !== value)
        : [...values[category], value]
    );
  };

  const activeFiltersCount = useMemo(() => {
    return (
      values.modelids.length +
      values.activityids.length +
      values.roleids.length +
      values.userids.length +
      values.groupids.length +
      (values.search ? 1 : 0)
      // values.userDataKeys.length
    );
  }, [values]);

  const activeSortsCount = useMemo(() => {
    return Object.keys(newSort).filter((key) => newSort[key]).length;
  }, [newSort]);

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const savedFilters = localStorage.getItem(
      `${LOCAL_STORAGE_KEY}-${organization.id}`
    );
    if (savedFilters) {
      setValues(JSON.parse(savedFilters));
    }
    setIsInitialized(true);
  }, [setValues, organization]);


  useEffect(() => {
    if (!isInitialized) return;

    localStorage.setItem(
      `${LOCAL_STORAGE_KEY}-${organization.id}`,
      JSON.stringify(values)
    );
  }, [values, organization, isInitialized]);

  //anti-filter-breaker
  useEffect(() => {
    if (
      pageData.processModels.loaded &&
      pageData.groups.loaded &&
      pageData.roles.loaded &&
      pageData.processes.loaded &&
      pageData.userDataKeys.loaded
    ) {
      const processModels = pageData.processModels.data;
      const groups = pageData.groups.data;
      const roles = pageData.roles.data;
      const processes = pageData.processes.data;
      const allUserDataKeys = pageData.userDataKeys.data;
      const values = JSON.parse(
        localStorage.getItem(`${LOCAL_STORAGE_KEY}-${organization.id}`) || ""
      );
      if (values) {
        const validModelIds = new Set(processModels.map((model) => model.id));
        const validActivityIds = new Set(
          processes.map((process) => process.flownodename)
        );
        const validUserIds = new Set(
          processes.map((process) => String(process.iduser))
        );
        const validGroupIds = new Set(groups.map((group) => String(group.id)));
        const validRoleIds = new Set(roles.map((role) => String(role.id)));
        const validUserDataKeys = new Set(
          allUserDataKeys.map((key) => String(key))
          // processes.flatMap(process => Object.keys(getUserData(process.process_userdata)))
        );

        const filteredModelIds = values.modelids.filter((id: number) =>
          validModelIds.has(id)
        );
        const filteredActivityIds = values.activityids.filter((id: string) =>
          validActivityIds.has(id)
        );
        const filteredUserIds = values.userids.filter((id: string) =>
          validUserIds.has(id)
        );
        const filteredGroupIds = values.groupids.filter((id: string) =>
          validGroupIds.has(id)
        );
        const filteredRoleIds = values.roleids.filter((id: string) =>
          validRoleIds.has(id)
        );
        const filteredUserDataKeys = values.userDataKeys.filter((key: string) =>
          validUserDataKeys.has(key)
        );

        // Nell'anti-filter-breaker useEffect, aggiungi:
        console.log('localStorage values:', values);
        console.log('validUserDataKeys:', validUserDataKeys);
        console.log('filteredUserDataKeys:', filteredUserDataKeys);

        if (
          filteredModelIds.length !== values.modelids.length ||
          filteredActivityIds.length !== values.activityids.length ||
          filteredUserIds.length !== values.userids.length ||
          filteredGroupIds.length !== values.groupids.length ||
          filteredRoleIds.length !== values.roleids.length ||
          filteredUserDataKeys.length !== values.userDataKeys.length
        ) {
          setValues({
            ...values,
            modelids: filteredModelIds,
            activityids: filteredActivityIds,
            userids: filteredUserIds,
            groupids: filteredGroupIds,
            roleids: filteredRoleIds,
            userDataKeys: filteredUserDataKeys,
          });
        }
      }
    }
  }, [pageData, setValues, organization.id]);

  return (
    <Stack height="100%" spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormGroup
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
              <Button
                id="switch-doing"
                variant="outlined"
                startIcon={doing ? <CheckIcon /> : undefined}
                onClick={() => {
                  const nextDoingState = !doing;
                  if (!toDo && !nextDoingState) {
                    setToDo(true);
                  }
                  setDoing(nextDoingState);
                }}
              >
                {getTranslation(
                  translations,
                  "pm.label.process_status.doing"
                )}
              </Button>
              <Button
                id="switch-todo"
                variant="outlined"
                startIcon={toDo ? <CheckIcon /> : undefined}
                onClick={() => {
                  const nextToDoState = !toDo;
                  if (!doing && !nextToDoState) {
                    setDoing(true);
                  }
                  setToDo(nextToDoState);
                }}
              >
                {getTranslation(
                  translations,
                  "pm.label.process_status.todo"
                )}
              </Button>
              <Badge
                badgeContent={values.userDataKeys?.length}
                color="primary"
                // sx={{ ml: 2 }}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <Button
                  id="userdata-menu"
                  onClick={handleUserDataClick}
                  variant="outlined"
                  startIcon={<FormatListBulletedIcon />}
                >
                  {getTranslation(translations, "generic.userdata.caption")}
                </Button>
              </Badge>
              <Badge
                badgeContent={activeFiltersCount}
                color="primary"
                // sx={{ ml: 2 }}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <Button
                  id="filter-menu"
                  onClick={handleFilterClick}
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                >
                  {getTranslation(translations, "generic.filters.caption")}
                </Button>
              </Badge>
              <Badge
                badgeContent={activeSortsCount}
                color="primary"
                // sx={{ ml: 2 }}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <Button
                  id="sort-menu"
                  onClick={handleSortClick}
                  variant="outlined"
                  // sx={{ ml: 2 }}
                  startIcon={<SortIcon />}
                >
                  {getTranslation(translations, "generic.sort.caption")}
                </Button>
              </Badge>
            </Box>
          </FormGroup>
        </Grid>
      </Grid>
      <Menu
        anchorEl={userDataMenuAnchorEl}
        open={Boolean(userDataMenuAnchorEl)}
        onClose={handleCloseUserData}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box p={2} component={Stack} sx={{ minWidth: 200 }}>
          <FormControl component="fieldset">
            {allUserDataKeys.length > 0 && (
              <Box display="flex" alignItems="center" justifyContent="space-around">
                <Typography component="legend" sx={{ fontWeight: "bold" }}>
                  {getTranslation(translations, "generic.userdata")}
                </Typography>
                <IconButton color="primary" onClick={() => handleClearUserdata()}>
                  <FilterListOffIcon />
                </IconButton>
              </Box>
            )}

            <MuiFormGroup>
              {allUserDataKeys
                .slice(0, showAllUserDataKeys ? allUserDataKeys.length : 5)
                .map((key) => (
                  <Tooltip key={key} title={key}>
                    <FormControlLabel
                      slotProps={{
                        typography: {
                          sx: {
                            maxWidth: 150,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        },
                      }}
                      control={
                        <Checkbox
                          id={`userdata-filter-${key}`}
                          checked={values.userDataKeys?.includes(key)}
                          onChange={() =>
                            handleCheckboxChange("userDataKeys", key)
                          }
                          name={key}
                        />
                      }
                      label={key}
                    />
                  </Tooltip>
                ))}
              {allUserDataKeys.length > 5 && (
                <Button
                  onClick={() => setShowAllUserDataKeys(!showAllUserDataKeys)}
                  variant="text"
                  sx={{ mt: 1, mr: 22 }}
                >
                  {showAllUserDataKeys
                    ? getTranslation(translations, "generic.show.less")
                    : getTranslation(translations, "generic.show.all")}
                </Button>
              )}
            </MuiFormGroup>
          </FormControl>
        </Box>
      </Menu>
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleCloseFilter}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box component={Stack} p={2}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <TextField
              id="search-tasks-text-monitor"
              onKeyDown={(e) => e.stopPropagation()}
              label={getTranslation(
                translations,
                "pm.organizationchart.ricerca.placeholder"
              )}
              sx={{ flex: 1 }}
              size="small"
              onChange={({ target }) => changeHandler("search", target.value)}
              value={values.search}
            />
            <IconButton color="primary" onClick={() => handleClearFilters()}>
              <FilterListOffIcon />
            </IconButton>
          </Box>
          <Box minWidth={200}>
            {processModels.length > 0 && (
              <Typography
                variant="subtitle1"
                gutterBottom
                marginTop={2}
                fontWeight="bold"
              >
                {getTranslation(translations, "generic.filter.by.processmodel")}
              </Typography>
            )}
            <ProcessModelFilter
              processModelsId={values.modelids}
              onProcessModelsChange={(newModels) =>
                changeHandler("modelids", newModels)
              }
              options={processModels}
            // per fallback se processmodels trorna ad essere del tipo process
            // .map((model) => ({
            //   assignmentmode: 0,
            //   id: model.id,
            //   idorganization: model.idorganization,
            //   idprocessmodel: model.id,
            //   idswimlane: 0,
            //   idtype: model.modeltype,
            //   name: "",
            //   processmodelname: model.name,
            //   swimlanename: "",
            // }))
            />
          </Box>

          {/* <Grid item xs={12}>
        {!modelsLoading && processModels.length > 0 && (
          <FormControl>
            <FormLabel component="legend">
              {getTranslation(translations, "pm.modelname.label.caption")}
            </FormLabel>
            <Select
              multiple
              displayEmpty
              value={values.modelnames}
              onChange={(event) => {
                const {
                  target: { value },
                } = event;
                changeHandler("modelnames", value);
              }}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return "Filtra per modello di processo";
                }
                return selected.join(", ");
              }}
            >
              {processModels.map((processModel) => (
                <MenuItem key={processModel.id} value={processModel.name}>
                  <Checkbox
                    checked={values.modelnames.includes(processModel.name)}
                  />
                  <ListItemText primary={processModel.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Grid> */}

          <FormControl component="fieldset" sx={{ marginTop: 2 }}>
            {activities.length > 0 && (
              <MuiFormGroup sx={{ fontWeight: "bold" }}>
                {getTranslation(
                  translations,
                  "pm.associations.assigned_activity"
                )}
                {activities
                  .slice(0, showAllActivities ? activities.length : 5)
                  .map((activity) => (
                    <Tooltip title={activity} key={activity}>
                      <FormControlLabel
                        slotProps={{
                          typography: {
                            sx: {
                              maxWidth: 150,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            },
                          },
                        }}
                        control={
                          <Checkbox
                            id={`activity-filter-${activity}`}
                            checked={values.activityids.includes(activity)}
                            onChange={() =>
                              handleCheckboxChange("activityids", activity)
                            }
                            name={activity || ""}
                          />
                        }
                        label={
                          activity ? activity : getTranslation(translations, "activity.no_name")
                        }
                      />
                    </Tooltip>
                  ))}
                {activities.length > 5 && (
                  <Button
                    onClick={() => setShowAllActivities(!showAllActivities)}
                    variant="text"
                    sx={{ mt: 1, mr: 22 }}
                  >
                    {showAllActivities
                      ? getTranslation(translations, "generic.show.less")
                      : getTranslation(translations, "generic.show.all")}
                  </Button>
                )}
              </MuiFormGroup>
            )}
          </FormControl>
          <FormControl component="fieldset" sx={{ marginTop: 2 }}>
            {filteredUsers.length > 0 && (
              <Typography component="legend" sx={{ fontWeight: "bold" }}>
                {getTranslation(translations, "generic.user")}
              </Typography>
            )}
            <MuiFormGroup>
              {/* Visualizza solo i primi 5 utenti oppure tutti gli utenti se showAllUsers è true */}
              {filteredUsers
                .slice(0, showAllUsers ? filteredUsers.length : 5)
                .map((user) => (
                  <Tooltip
                    title={`${user.lastname} ${user.firstname}`}
                    key={user.id}
                  >
                    <FormControlLabel
                      slotProps={{
                        typography: {
                          sx: {
                            maxWidth: 150,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        },
                      }}
                      control={
                        <Checkbox
                          id={`users-filter-${user.id}`}
                          checked={values.userids.includes(String(user.id))}
                          onChange={() =>
                            handleCheckboxChange("userids", String(user.id))
                          }
                          name={`${user.lastname} ${user.firstname}`}
                        />
                      }
                      label={`${user.lastname} ${user.firstname}`}
                    />
                  </Tooltip>
                ))}

              {/* Mostra il pulsante "Show All" o "Show Less" solo se ci sono più di 5 utenti */}
              {filteredUsers.length > 5 && (
                <Button
                  onClick={() => setShowAllUsers(!showAllUsers)} // Alterna tra mostrare tutti gli utenti e mostrarne solo 5
                  variant="text"
                  sx={{ mt: 1, mr: 2 }}
                >
                  {showAllUsers
                    ? getTranslation(translations, "generic.show.less") // true
                    : getTranslation(translations, "generic.show.all")}
                </Button>
              )}
            </MuiFormGroup>
            {/* {groups.length > 0 && (
                <Typography component="legend" sx={{ fontWeight: "bold" }}>
                  {getTranslation(translations, "groups.name")}
                </Typography>
              )} */}
            {/* <MuiFormGroup>
                {groups
                  .slice(0, showAllGroups ? groups.length : 5)
                  .map((group) => (
                    <Tooltip title={group.name} key={group.id}>
                      <FormControlLabel
                        slotProps={{
                          typography: {
                            sx: {
                              maxWidth: 150,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            },
                          },
                        }}
                        control={
                          <Checkbox
                            id={`group-filter-${group.id}`}
                            checked={values.groupids.includes(String(group.id))}
                            onChange={() =>
                              handleCheckboxChange("groupids", String(group.id))
                            }
                            name={group.name}
                          />
                        }
                        label={group.name}
                      />
                    </Tooltip>
                  ))}
                {groups.length > 5 && (
                  <Button
                    onClick={() => setShowAllGroups(!showAllGroups)}
                    variant="text"
                    sx={{ mt: 1, mr: 2 }}
                  >
                    {showAllGroups
                      ? getTranslation(translations, "generic.show.less")
                      : getTranslation(translations, "generic.show.all")}
                  </Button>
                )}
              </MuiFormGroup> */}
          </FormControl>
          {/* {roles.length > 0 && (
              <Typography component="legend" sx={{ fontWeight: "bold" }}>
                {getTranslation(translations, "placeholders.role.name.label")}
              </Typography>
            )} */}
          {/* <MuiFormGroup>
              {roles.slice(0, showAllRoles ? roles.length : 5).map((role) => (
                <Tooltip key={role.id} title={role.name}>
                  <FormControlLabel
                    slotProps={{
                      typography: {
                        sx: {
                          maxWidth: 150,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      },
                    }}
                    control={
                      <Checkbox
                        id={`role-filter-${role.id}`}
                        checked={values.roleids.includes(String(role.id))}
                        onChange={() =>
                          handleCheckboxChange("roleids", String(role.id))
                        }
                        name={role.name}
                      />
                    }
                    label={role.name}
                  />
                </Tooltip>
              ))}
              {roles.length > 5 && (
                <Button
                  onClick={() => setShowAllRoles(!showAllRoles)}
                  variant="text"
                  sx={{ mt: 1, mr: 22 }}
                >
                  {showAllRoles
                    ? getTranslation(translations, "generic.show.less")
                    : getTranslation(translations, "generic.show.all")}
                </Button>
              )}
            </MuiFormGroup> */}
          {/* <FormControl component="fieldset" sx={{ marginTop: 2 }}>
            {allUserDataKeys.length > 0 && (
              <Typography component="legend" sx={{ fontWeight: "bold" }}>
                {getTranslation(translations, "generic.userdata")}
              </Typography>
            )}
            <MuiFormGroup>
              {allUserDataKeys
                .slice(0, showAllUserDataKeys ? allUserDataKeys.length : 5)
                .map((key) => (
                  <Tooltip key={key} title={key}>
                    <FormControlLabel
                      slotProps={{
                        typography: {
                          sx: {
                            maxWidth: 150,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        },
                      }}
                      control={
                        <Checkbox
                          id={`userdata-filter-${key}`}
                          checked={values.userDataKeys.includes(key)}
                          onChange={() =>
                            handleCheckboxChange("userDataKeys", key)
                          }
                          name={key}
                        />
                      }
                      label={key}
                    />
                  </Tooltip>
                ))}
              {allUserDataKeys.length > 5 && (
                <Button
                  onClick={() => setShowAllUserDataKeys(!showAllUserDataKeys)}
                  variant="text"
                  sx={{ mt: 1, mr: 22 }}
                >
                  {showAllUserDataKeys
                    ? getTranslation(translations, "generic.show.less")
                    : getTranslation(translations, "generic.show.all")}
                </Button>
              )}
            </MuiFormGroup>
          </FormControl> */}
        </Box>
      </Menu>
      <Menu
        id="sort-menu"
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={handleSortClose}
        anchorOrigin={{
          // ma perché non ci va
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box p={2} component={Stack} sx={{ minWidth: 200 }}>
          <Typography variant="subtitle1" gutterBottom>
            {getTranslation(translations, "generic.sort.caption")}
          </Typography>
          <TextField
            select
            label={getTranslation(translations, "generic.sort.caption")}
            size="small"
            onChange={({ target }) => setSortField(target.value)}
            value={sortField || ""}
          >
            <MenuItem>
              {getTranslation(translations, "generic.filter.none")}
            </MenuItem>
            <MenuItem value="process">
              {getTranslation(
                translations,
                "generic.filter.data.start.process"
              )}
            </MenuItem>
            <MenuItem value="activityStart">
              {getTranslation(
                translations,
                "generic.filter.data.start.activity"
              )}
            </MenuItem>
          </TextField>
          {sortField && (
            <TextField
              select
              label={getTranslation(translations, "generic.sort.caption")}
              size="small"
              onChange={({ target }) =>
                setNewSort({ [sortField]: Number(target.value) })
              }
              value={newSort[sortField] || SortState.None}
              sx={{ mt: 2 }}
            >
              <MenuItem value={SortState.None}>
                {getTranslation(translations, "generic.filter.none")}
              </MenuItem>
              <MenuItem value={SortState.Asc}>
                {getTranslation(translations, "generic.filter.orderby.asc")}
              </MenuItem>
              <MenuItem value={SortState.Desc}>
                {getTranslation(translations, "generic.filter.orderby.desc")}
              </MenuItem>
            </TextField>
          )}
        </Box>
      </Menu>
      {loading ?
        <CircularProgress sx={{ alignSelf: "center" }} />
        :
        <ProcessesList
          handleChangeSort={handleChangeSort}
          newSort={newSort}
          refresh={fetchData}
          title={getTranslation(translations, "generic.processes.monitor")}
          processes={sortedProcesses}
          filters={values}
          filteredUserDataKeys={values.userDataKeys}
        />
      }
    </Stack>
  );
};

export default ProcessStarted;
